import { useDebounceEffect } from './../../app/useDebounceEffect';
import { imgPreview } from './../../components/files/imgPreview';
import { canvasPreview } from './../../components/files/canvasPreview';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import LoadingAnimation from '../loading/loading';

import 'react-image-crop/dist/ReactCrop.css'
import './image-cropper.css';

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
    mediaWidth,
    mediaHeight,
    aspect,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    )
}
export const ImageCropper = forwardRef(({ src, croppedFileName, onCropCompleted, children }, ref) => {


    const [imgSrc, setImgSrc] = useState('');
    const previewCanvasRef = useRef(null);
    const imgRef = useRef(null);

    const [crop, setCrop] = useState({ aspect: 1, x: 0, y: 0, width: 300, height: 300, unit: "px" });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);

    const imgOnLoad = async img => {
        imgRef.current = img;

        const { width, height } = img;
        const newCrop = centerAspectCrop(width, height, 1);
        setCrop(newCrop);

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(blob => {
            const url = URL.createObjectURL(blob);
            setImgSrc(url);
        }, 'image/jpeg');

        const previewBlob = await imgPreview(img, newCrop, croppedFileName);
        setCroppedImage(previewBlob.blob);
    };

    useEffect(() => {

        if (src) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = src;
            img.onload = async () => await imgOnLoad(img);
        }
    }, [src]);

    useDebounceEffect(
        async () => {
            if (
                completedCrop && completedCrop.width &&
                completedCrop.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {

                //We use canvasPreview as it's much faster than imgPreview.
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop
                );
            }
        },
        100,
        [completedCrop]
    );

    // Expose methods to the parent component
    useImperativeHandle(ref, () => ({
        getState: () => { return { imgRef, previewCanvasRef, crop, croppedImage } },
        isCropped: () => !!completedCrop,
        reset: () => setCompletedCrop(null)
    }));

    const handleCropCompleted = (crop, ref) => {
        setCompletedCrop(crop);
        onCropCompleted(ref)
    };

    return (
        <>
            {!imgSrc && <LoadingAnimation />}
            {!!imgSrc && (
                <ReactCrop
                    src={imgSrc}
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={c => handleCropCompleted(c, ref)}
                    aspect={1}
                    circularCrop
                >
                    <div id="gallery" className="gallery">
                        <div className="image">
                            <div className="image-inner image-cropper-container">
                                <img ref={imgRef} src={imgSrc} alt='Crop Me' />
                                {children}
                            </div>

                        </div>
                    </div>
                </ReactCrop>
            )}
            {!!completedCrop && (
                <div>
                    <canvas
                        ref={previewCanvasRef}
                        style={{ display: "none" }}
                    />
                </div>
            )}
        </>
    );
});